import type { Logger } from "@expert/logging";

import ky, { HTTPError } from "ky";

import type { DefaultPayload } from "./types";
import type { EncryptedVerizonPin } from "./verizonPinEncryption";

import { defaultHeaders, defaultTimeoutInMS } from "../types";

/** The PIN travels only as PIE ciphertext: `encryptedPin` with the `keyId` and `phaseBit` it was made with. */
type VerifyVerizonCustomerPayload = DefaultPayload & EncryptedVerizonPin & { mdn: string };

interface VerifyVerizonCustomerResponse {
    verified?: boolean;
    token?: string;
    code?: string | null;
}

/**
 * The verification outcome the guide routes on:
 * - `VERIFIED`: `token` proves the line was verified; `prepareVerizonReplacement` sends it.
 * - `INCORRECT_PIN`: Horizon rejected the PIN, and the expert may try again.
 * - `MAX_ATTEMPTS`: the account PIN is locked, so the expert finishes in ACSS.
 * - `REQUEST_FAILED`: anything else (403 when the expert isn't enabled, 400, 500, network, an unknown
 *   code), which also sends the expert to the manual flow.
 */
export type VerifyVerizonCustomerResult =
    | { outcome: "VERIFIED"; token: string }
    | { outcome: "INCORRECT_PIN" | "MAX_ATTEMPTS" }
    | { outcome: "REQUEST_FAILED"; errorCode: string };

/**
 * Verifies the Verizon customer's account PIN for the line, the Verizon counterpart of `verifyCustomer`.
 * Unlike AT&T's, the PIN is never sent: the caller encrypts it with `encryptVerizonPin` first.
 */
export const verifyVerizonCustomer = async (
    ssoAccessToken: string,
    payload: VerifyVerizonCustomerPayload,
    logger: Logger,
    apiBaseUrl: string,
): Promise<VerifyVerizonCustomerResult> => {
    try {
        const url = `${apiBaseUrl}/api/device-activation/v1/verizon/verify-customer`;
        const resp = await ky.post(url, {
            headers: {
                ...defaultHeaders,
                Authorization: `Bearer ${ssoAccessToken}`,
            },
            json: payload,
            timeout: defaultTimeoutInMS,
        });

        const { verified, token, code } = await resp.json<VerifyVerizonCustomerResponse>();
        if (verified === true && token) return { outcome: "VERIFIED", token };
        if (verified === false && code == null) return { outcome: "INCORRECT_PIN" };
        if (verified === false && code === "MAX_ATTEMPTS") return { outcome: "MAX_ATTEMPTS" };

        logger.warn(
            { verizonVerifyCode: code, hasVerizonVerifyToken: !!token },
            "Solve Guide | Error: Unexpected Verizon customer verification response.",
        );
        return { outcome: "REQUEST_FAILED", errorCode: code ?? "" };
    } catch (error) {
        logger.warn({ error }, "Solve Guide | Error: Could not verify the Verizon customer.");

        let errorCode = "";
        if (error instanceof HTTPError) {
            try {
                const errorJson = await error.response.json<{ code?: string }>();
                errorCode = errorJson.code ?? "";
            } catch {
                errorCode = "";
            }
        }

        return { outcome: "REQUEST_FAILED", errorCode };
    }
};
